import React, { useState, useEffect, useCallback } from 'react';
import { Save, Plus, AlertTriangle, FileText, CheckCircle } from 'lucide-react';
import { getConfirmationPage, updateConfirmationPage, createConfirmationPage } from '../utils/confirmationPageApi';
import { uploadFileToPublic } from '../utils/fileUpload';
import { S3_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import SubmitNotification from '../components/SubmitNotification';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ConfirmationPage = () => {
  const { userSites } = useAuth();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [submitNotification, setSubmitNotification] = useState(null);

  // WYSIWYG Editor Configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'align', 'link', 'image'
  ];

  const [formData, setFormData] = useState({
    content_id: '',
    title: '',
    banner: '',
    content: '',
    sub_content: '',
    updated_at: ''
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Ensure contents is always an array
  const safeContents = Array.isArray(contents) ? contents : [];

  // Handle userSites - it can be an array or an object, get first element if array
  const siteData = Array.isArray(userSites) ? (userSites.length > 0 ? userSites[0] : null) : userSites;

  // Fetch contents from API based on user's site ID
  const fetchContents = useCallback(async () => {
    console.log('🔍 fetchContents called with userSites:', userSites);
    console.log('🔍 Using siteData:', siteData);

    if (!siteData || !siteData.site_id) {
      console.warn('❌ No site ID available for user:', siteData);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🚀 Fetching confirmation page content for site_id:', siteData.site_id);
      const response = await getConfirmationPage({ site_id: siteData.site_id });
      
      console.log('📡 API Response:', response);
      setApiResponse(response);
      
      if (response.success) {
        let contentData = response.data;
        
        // Handle different API response structures
        if (response.data && response.data.data) {
          contentData = response.data.data;
        } else if (response.data && response.data.contents) {
          contentData = response.data.contents;
        } else if (response.data && response.data.items) {
          contentData = response.data.items;
        } else if (response.data && response.data.results) {
          contentData = response.data.results;
        } else if (response.data && response.data.list) {
          contentData = response.data.list;
        } else if (response.data && response.data.records) {
          contentData = response.data.records;
        }
        
        console.log('📋 Processed content data:', contentData);
        
        if (Array.isArray(contentData)) {
          // Transform data to ensure content_id field exists (in case API returns 'id' instead)
          const transformedData = contentData.map(content => ({
            ...content,
            content_id: content.content_id || content.id || ''
          }));
          
          console.log('✅ Setting contents from API:', transformedData);
          setContents(transformedData);
          
          // Select content based on site ID
          const siteContent = transformedData.find(content => 
            content.site_id === siteData?.site_id || 
            content.site_id === String(siteData?.site_id)
          );
          
          if (siteContent) {
            console.log('🎯 Found content for site ID:', siteData?.site_id, siteContent);
            setSelectedContent(siteContent);
            setFormData({
              content_id: siteContent.content_id || '',
              title: siteContent.title || '',
              banner: siteContent.banner || '',
              content: siteContent.content || '',
              sub_content: siteContent.sub_content || '',
              updated_at: siteContent.updated_at || ''
            });
          } else {
            console.log('⚠️ No content found for site ID:', siteData?.site_id);
            setSelectedContent(null);
          }
        } else {
          console.warn('❌ API returned data but it\'s not an array:', contentData);
          setContents([]);
          setSelectedContent(null);
        }
      } else {
        console.warn('❌ API call failed:', response.error);
        setContents([]);
        setSelectedContent(null);
      }
    } catch (error) {
      console.warn('❌ API error:', error);
      // Fallback to mock data if API fails
      const mockData = [
        {
          content_id: 'CONF001',
          site_id: siteData?.site_id,
          title: 'Thank You!',
          banner: '',
          content: '<h1>Thank You for Completing the Survey!</h1><p>Your response has been successfully submitted.</p><p>We appreciate your time and valuable feedback.</p>',
          sub_content: '<p>You will receive a confirmation email shortly.</p>',
          updated_at: new Date().toISOString()
        }
      ];
      
      setApiResponse({ success: false, error: error.message });
      setContents(mockData);
      
      // Select mock data based on site ID
      const siteMockContent = mockData.find(content => 
        content.site_id === siteData?.site_id || 
        content.site_id === String(siteData?.site_id)
      );
      
      if (siteMockContent) {
        console.log('🎯 Found mock content for site ID:', siteData?.site_id, siteMockContent);
        setSelectedContent(siteMockContent);
        setFormData({
          content_id: siteMockContent.content_id || '',
          title: siteMockContent.title || '',
          banner: siteMockContent.banner || '',
          content: siteMockContent.content || '',
          sub_content: siteMockContent.sub_content || '',
          updated_at: siteMockContent.updated_at || ''
        });
      } else {
        console.log('⚠️ No mock content found for site ID:', siteData?.site_id);
        setSelectedContent(null);
      }
    } finally {
      setLoading(false);
    }
  }, [userSites]);

  // Fetch contents on component mount
  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitNotification({ type: 'loading', message: isCreateMode ? 'Creating confirmation page...' : 'Updating confirmation page...' });
    console.log('🚀 handleSubmit called, isCreateMode:', isCreateMode);
    console.log('📋 Form data:', formData);
    console.log('🎯 Selected content:', selectedContent);
    
    try {
      let response;
      if (isCreateMode) {
        const createData = {
          ...formData,
          site_id: siteData?.site_id
        };
        console.log('📤 Creating confirmation page content with data:', createData);
        response = await createConfirmationPage(createData);
      } else {
        if (!selectedContent || !selectedContent.content_id) {
          console.error('❌ No confirmation page content available to update');
          setSubmitNotification({ type: 'error', message: 'No confirmation page content available to update.' });
          return;
        }
        console.log('📤 Updating confirmation page content with ID:', selectedContent.content_id);
        response = await updateConfirmationPage(selectedContent.content_id, formData);
      }

      console.log('📡 Submit response:', response);

      if (response.success) {
        console.log('✅ Confirmation page content operation successful');
        if (isCreateMode) {
          setSubmitNotification({ type: 'success', message: 'Confirmation page content created successfully!' });
          await fetchContents();
          setIsCreateMode(false);
        } else {
          setSubmitNotification({ type: 'success', message: 'Confirmation page content updated successfully!' });
          await fetchContents();
        }
      } else {
        console.error('❌ Confirmation page content operation failed:', response.error);
        setSubmitNotification({ type: 'error', message: `Failed to ${isCreateMode ? 'create' : 'update'} confirmation page content: ${response.error || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      setSubmitNotification({ type: 'error', message: `Error ${isCreateMode ? 'creating' : 'updating'} confirmation page content: ${error.message}` });
    }
  };

  const resetForm = () => {
    setFormData({
      content_id: '',
      title: '',
      banner: '',
      content: '',
      sub_content: '',
      updated_at: ''
    });
  };

  const handleCreateMode = () => {
    setIsCreateMode(true);
    resetForm();
  };

  const handleUpdateMode = () => {
    setIsCreateMode(false);
    
    // Find content for current site ID (ensure content_id field exists)
    const siteContent = safeContents.find(content => {
      const contentWithId = {
        ...content,
        content_id: content.content_id || content.id || ''
      };
      return contentWithId.site_id === siteData?.site_id || 
             contentWithId.site_id === String(siteData?.site_id);
    });
    
    if (siteContent) {
      // Ensure content_id field exists
      const contentWithId = {
        ...siteContent,
        content_id: siteContent.content_id || siteContent.id || ''
      };
      
      console.log('🎯 Switching to update mode for site content:', contentWithId);
      setSelectedContent(contentWithId);
      setFormData({
        content_id: contentWithId.content_id || '',
        title: contentWithId.title || '',
        banner: contentWithId.banner || '',
        content: contentWithId.content || '',
        sub_content: contentWithId.sub_content || '',
        updated_at: contentWithId.updated_at || ''
      });
    } else {
      console.log('⚠️ No content found for site ID in update mode:', siteData?.site_id);
      setSelectedContent(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SubmitNotification notification={submitNotification} onDismiss={() => setSubmitNotification(null)} />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Confirmation Page</h1>
          <p className="text-gray-600">
            {isCreateMode ? 'Create new confirmation page content template.' : 'Update confirmation page content templates.'}
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          {isCreateMode && (
            <button 
              onClick={handleUpdateMode}
              className="btn-secondary flex items-center space-x-2"
            >
              <span>Edit Existing</span>
            </button>
          )}
        </div>
      </div>

      {/* Update Form */}
      <div className="card">
         {/**<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
         <h3 className="text-lg font-medium text-gray-900">
            {isCreateMode ? 'Create Confirmation Page Template' : 'Update Confirmation Page Template'}
          </h3>
          
           * <p className="text-sm text-gray-600">
            {isCreateMode 
              ? 'Fill in the details to create a new confirmation page template.'
              : selectedContent 
                ? `Editing: ${selectedContent.title}` 
                : 'Loading confirmation page template...'
            }
          </p>
          {userSites && userSites.site_id && (
            <p className="text-xs text-gray-500">Site ID: {userSites.site_id}</p>
          )}
          
        </div>*/}
        
        <div className="p-6">
          {(selectedContent || isCreateMode) ? (
            <form onSubmit={handleSubmit} className="space-y-6">
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <ReactQuill
                      value={formData.title}
                      onChange={(value) => setFormData({...formData, title: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter confirmation page title..."
                      style={{ minHeight: '100px' }}
                    />
                  </div>
                </div>
                
                

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banner Upload
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="banner-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>{uploading ? 'Uploading...' : 'Upload a file'}</span>
                          <input
                            id="banner-upload"
                            name="banner-upload"
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            disabled={uploading}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  setUploading(true);
                                  setUploadError(null);
                                  console.log('📤 Starting file upload:', file.name);
                                  
                                  const filePath = await uploadFileToPublic(file, 'banners');
                                  console.log('✅ File uploaded successfully:', filePath);
                                  
                                  setFormData({...formData, banner: filePath});
                                } catch (error) {
                                  console.error('❌ File upload failed:', error);
                                  setUploadError(error.message);
                                  alert('Failed to upload file: ' + error.message);
                                } finally {
                                  setUploading(false);
                                }
                              }
                            }}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      {uploading && (
                        <p className="text-sm text-blue-600">Uploading banner...</p>
                      )}
                      {uploadError && (
                        <p className="text-sm text-red-600">Error: {uploadError}</p>
                      )}
                      {formData.banner && !uploading && (
                        <div className="mt-2">
                          <p className="text-sm text-green-600">✓ Banner uploaded successfully</p>
                            <img src={`${S3_BASE_URL}/${formData.banner}`} alt="Banner preview" className="mt-2 max-h-32 rounded" />
                         
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <ReactQuill
                      value={formData.content}
                      onChange={(value) => setFormData({...formData, content: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter main confirmation content..."
                      style={{ minHeight: '200px' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sub Content
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <ReactQuill
                      value={formData.sub_content}
                      onChange={(value) => setFormData({...formData, sub_content: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter sub content..."
                      style={{ minHeight: '150px' }}
                    />
                  </div>
                </div>
              
              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={submitNotification?.type === 'loading'}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  <span>{submitNotification?.type === 'loading' ? 'Saving...' : (isCreateMode ? 'Create Content' : 'Update Content')}</span>
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-500">No confirmation page template found</p>
              <p className="text-sm text-gray-400 mb-4">
                {siteData && siteData.site_id 
                  ? `No confirmation page template available for site ID: ${siteData.site_id}`
                  : 'Unable to load confirmation page template - no site ID available'
                }
              </p>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Please refresh the page to retry loading</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;

